import React from "react";
import { isAnyRecordCodec, isListOfStrings, TAnyCodec, TAnyListCodec, TAnyRecordCodec, TTypeOfNewDefault } from "../../../../shared/src/codecs/codec";
import CRMInputLabelAndErrorWrapComponent from "../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { array, option, record } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { CRMSpacingRow } from "../CRMSpacingRow/CRMSpacingRow";
import { CRMSpacer } from "../CRMSpacer/CRMSpacer";
import { CRMSpacingColumn } from "../CRMSpacingColumn/CRMSpacingColumn";
import { CRMTitleSubSection } from "../CRMTitleSubSection/CRMTitleSubSection";
import { TAnyIntersectionCodec } from "../../../../shared/src/codecs/types/intersection";
import { 
    isUnionOfLiterals,
    isUnionOfNullAndBoolean,
    isUnionOfNullAndCurrencyInteger,
    isUnionOfNullAndDate,
    isUnionOfNullAndDateTime,
    isUnionOfNullAndDecimal,
    isUnionOfNullAndDeferDateTime,
    isUnionOfNullAndInteger,
    isUnionOfNullAndOneOtherType,
    TAnyUnionCodec
} from "../../../../shared/src/codecs/types/union";
import { TAnyLiteralCodec } from "../../../../shared/src/codecs/types/literal";
import { doesErrorKeyExist, TError } from "../../../../shared/src/codecs/errors";
import { CRMMultiSelectDropdownComponent } from "../CRMMultiSelectDropdownComponent/CRMMultiSelectDropdownComponent";
import { requireExhaustive, snakeCaseToCopyText } from "../../../../shared/src/util";
import { CRMDateTimeOrNullYesNoRadioFormInput } from "../CRMFormInputs/CRMDateTimeOrNullYesNoRadioFormInput";
import { CRMTelFormInput } from "../CRMFormInputs/CRMTelFormInput";
import { CRMNumericStringFormInput } from "../CRMFormInputs/CRMNumericStringFormInput";
import { CRMEmailFormInput } from "../CRMFormInputs/CRMEmailFormInput";
import { CRMTextFormInput } from "../CRMFormInputs/CRMTextFormInput";
import { CRMTextAreaFormInput } from "../CRMFormInputs/CRMTextAreaFormInput";
import { CRMDecimalFormInput } from "../CRMFormInputs/CRMDecimalFormInput";
import { CRMPositiveDecimalFormInput } from "../CRMFormInputs/CRMPositiveDecimalFormInput";
import { CRMYesNoBooleanRadioFormInput } from "../CRMFormInputs/CRMYesNoBooleanRadioFormInput";
import { CRMCalendarDateWithoutTimeFormInput } from "../CRMFormInputs/CRMCalendarDateWithoutTimeFormInput";
import { CRMCalendarDateTimeFormInput } from "../CRMFormInputs/CRMCalendarDateTimeFormInput";
import { CRMDateWithoutTimeOrNullYesNoRadioFormInput } from "../CRMFormInputs/CRMDateWithoutTimeOrNullYesNoRadioFormInput";
import { CRMAdditiveArrayFormInput } from "../CRMFormInputs/CRMAdditiveArrayFormInput";
import { TCRMFormInput } from "../CRMFormInputs/CRMFormInputTypes";
import { CRMRadioButtonsFormInput } from "../CRMFormInputs/CRMRadioButtonsFormInput";
import { CRMDeferInputWithLabelFormInput } from "../CRMFormInputs/CRMDeferInputWithLabelFormInput";
import { CRMDeferInputWithoutLabelFormInput } from "../CRMFormInputs/CRMDeferInputWithoutLabelFormInput";
import { CRMYesNoBooleanWithClearRadioFormInput } from "../CRMFormInputs/CRMYesNoBooleanWithClearRadioFormInput";
import { CRMDropdownFormInput } from "../CRMFormInputs/CRMDropdownFormInput";
import { CRMCalendarDateTimeWithClearFormInput } from "../CRMFormInputs/CRMCalendarDateTimeWithClearFormInput";
import { CRMCalendarDateWithoutTimeWithClearFormInput } from "../CRMFormInputs/CRMCalendarDateWithoutTimeWithClearFormInput";
import { CRMCurrencyFormInput } from "../CRMFormInputs/CRMCurrencyFormInput";
import { Text } from "../BuildingBlocks/Text";
import { CRMPasswordFormInput } from "../CRMFormInputs/CRMPasswordFormInput";
import { CRMCodecEditFormFieldHelp, TCodecEditFormFieldHelp } from "./CRMCodecEditFormFieldHelp";

type TCRMCodecEditFormComponentProps<C extends TAnyCodec> = {
    propName?: string;
    codec: C,
    model: TTypeOfNewDefault<C>,
    validationErrors: TError,
    onChange: (m: TTypeOfNewDefault<C>) => void,
    columns?: number;
    showDeferDateLabels?: boolean;
    disabled?: boolean;
    errorKeyPrefix?: string;
    overrideComps?: {
        [K in keyof TTypeOfNewDefault<C>]?: TCRMFormInput<TTypeOfNewDefault<C>[K]>
    }
    hideFields?: Array<keyof TTypeOfNewDefault<C> extends string ? keyof TTypeOfNewDefault<C> : never>
    fieldHelp?: {
        [K in keyof TTypeOfNewDefault<C>]?: TCodecEditFormFieldHelp | null
    }
};

// registration_land_reg_link holds an HM Land Registry application reference, not a URL —
// a derived "link" label tells a fee-earner to paste the wrong thing.
const labelOverrides: Record<string, string> = {
    retention_reason: "What the money is for",
    retention_chase_from: "Start chasing from",
    retention_settled_at: "Settled",
    appointed_agent_agreement_reference: "Agent agreement reference",
    appointed_agent_agreement_signed_on: "Agent agreement signed",
    registration_land_reg_link: "Land Registry reference",
    buyer_covers_seller_legal_fees: "Buyer covers the seller's legal fees",
    buyer_covers_seller_auction_fee: "Buyer covers the seller's auction fee",
    sell_with_tenant_in_situ: "Sold with a tenant in situ",
};

export const propNameToLabel = (propName: string | undefined): string =>
    propName
        ? labelOverrides[propName] ?? snakeCaseToCopyText(propName.replace(/_pence$/, ""))
        : "";

const CRMCodecEditFormComponent = <C extends TAnyCodec>(props: TCRMCodecEditFormComponentProps<C>): JSX.Element => {
    return (
        <CRMSpacingColumn spacing="medium">
            {pipe(
                getListOfFormComponents({...props, showDeferDateLabels: props.showDeferDateLabels ?? false, inputDisabled: props.disabled}),
                array.chunksOf(props.columns || 1),
                array.mapWithIndex((index, components) =>
                    <CRMSpacingRow
                        key={index}
                        spacing="medium"
                        alignItems="flex-start"
                        childSize={getSpacingChildSizeByColumns(props.columns || 1)}
                    >
                        {
                            pipe(
                                components,
                                array.mapWithIndex((i, component) =>
                                    <React.Fragment key={i}>
                                        {component}
                                    </React.Fragment>
                                )
                            )
                        }
                    </CRMSpacingRow>
                ),
            )}
        </CRMSpacingColumn>
    );
}

export const CRMCodecEditForm = React.memo(CRMCodecEditFormComponent) as typeof CRMCodecEditFormComponent;

const getListOfFormComponents = <C extends TAnyCodec>(props: {
    propName?: string;
    errorKeyPrefix?: string;
    codec: C,
    model: TTypeOfNewDefault<C>,
    validationErrors: TError,
    onChange: (m: TTypeOfNewDefault<C>) => void,
    showDeferDateLabels: boolean,
    inputDisabled?: boolean,
    overrideComps?: {
        [K in keyof TTypeOfNewDefault<C>]?: TCRMFormInput<TTypeOfNewDefault<C>[K]>
    },
    hideFields?: Array<string>,
    fieldHelp?: Record<string, TCodecEditFormFieldHelp | null | undefined>
}): Array<JSX.Element> => {
    const errorKey = `${props.errorKeyPrefix ?? "edited"}.${props.propName}`;

    switch (props.codec.type) {
        // TEXT INPUT - GENERIC
        case "StringCodec":
        case "NonEmptyStringCodec":
        case "RegexCodec":
            return [
                <CRMTextFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]
        case "PasswordCodec":
            return [
                <CRMPasswordFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]
        case "PostcodeCodec":
            return [
                <CRMTextFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]
        // TEXT INPUT NUMERIC
        case "DecimalCodec":
            return [
                <CRMDecimalFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as number}
                    onChange={props.onChange as (s: number | null) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]
        case "PositiveDecimalCodec":
            return [
                <CRMPositiveDecimalFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as number}
                    onChange={props.onChange as (s: number | null) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]
        case "CurrencyIntegerCodec":
            return [
                <CRMCurrencyFormInput 
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as number | null}
                    onChange={props.onChange as (v: number | null) => void}
                    disabled={props.inputDisabled}
                    mode={props.propName?.endsWith("_pence") ? "pence" : "pound"}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        // TEXT INPUT - DISABLED
        case "LiteralCodec":
        case "UuidCodec":
            return [
                <CRMTextFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={true}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        // TEXT INPUT - EMAIL
        case "EmailCodec":
            return [
                <CRMEmailFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        // TEXT INPUT - NUMBER
        case "IntegerCodec":
        case "GreaterThanCodec":
        case "PositiveIntegerCodec":
            return [
                <CRMNumericStringFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={(props.model as number) === 0 ? "" : (props.model as number).toString()}
                    onChange={(s: string) =>  props.onChange(s === "" ? 0 as TTypeOfNewDefault<C> : parseInt(s) as TTypeOfNewDefault<C>)}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        // TEXT INPUT - NUMBER
        case "PhoneNumberCodec":
            return [
                <CRMTelFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        // TEXTAREA 
        case "LongStringCodec":
            return [
                <CRMTextAreaFormInput 
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]
            
        // RADIO INPUT
        case "BooleanCodec":
            return [
                <CRMYesNoBooleanRadioFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as boolean}
                    onChange={props.onChange as (val: boolean) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        // CALENDAR INPUT
        case "DateCodec":
            return [
                <CRMCalendarDateWithoutTimeFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]
        
        // CALENDAR INPUT - LOCAL TIME
        case "DateTimeCodec":
            return [
                <CRMCalendarDateTimeFormInput
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string}
                    onChange={props.onChange as (val: string) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        case "DateOrNullAsBooleanCodec":
            return [
                <CRMDateWithoutTimeOrNullYesNoRadioFormInput 
                    label={props.propName ? propNameToLabel(props.propName) : ""}
                    model={props.model as string | null}
                    onChange={props.onChange as (val: string | null) => void}
                    disabled={props.inputDisabled}
                    displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
                />
            ]

        case "DateTimeOrNullAsBooleanCodec":
            return [<CRMDateTimeOrNullYesNoRadioFormInput
                label={props.propName ? propNameToLabel(props.propName) : ""}
                model={props.model as string | null}
                onChange={props.onChange as (val: string | null) => void}
                disabled={props.inputDisabled}
                displayError={doesErrorKeyExist(errorKey, props.validationErrors)}
            />];

        case "RequiredCodec":
        case "PartialCodec":
        case "RequiredFlatOverloadedCodec":
            return renderRecordForm(
                props.propName,
                props.errorKeyPrefix,
                props.codec, 
                props.model, 
                props.onChange,
                props.validationErrors,
                props.showDeferDateLabels,
                props.inputDisabled,
                props.overrideComps,
                props.hideFields,
                props.fieldHelp
            );
        
        case "ArrayCodec":
        case "UniqueArrayCodec":
        case "NonEmptyArrayCodec":
        case "FixedLengthArrayCodec":
            return renderArrayForm(
                props.propName || "",
                props.errorKeyPrefix,
                props.codec,
                props.model as Array<unknown>,
                props.onChange as (m: unknown[]) => void,
                props.validationErrors,
                props.showDeferDateLabels,
                props.inputDisabled,
            );
        
        case "OverloadCodec":
        case "CustomCodec":
            return [
                <CRMCodecEditForm
                    propName={props.propName}
                    codec={props.codec.payload.codec as C}
                    model={props.model}
                    validationErrors={props.validationErrors}
                    onChange={props.onChange}
                    disabled={props.inputDisabled}
                />
            ];

        case "IntersectionCodec":
            return renderIntersectionForm(
                props.propName,
                props.errorKeyPrefix,
                props.codec,
                props.model,
                props.onChange,
                props.validationErrors,
                props.showDeferDateLabels,
                props.inputDisabled,
            );
        
        case "UnionCodec":
            return renderUnionForm(
                props.propName,
                props.errorKeyPrefix,
                props.codec,
                props.model,
                props.onChange,
                props.validationErrors,
                props.showDeferDateLabels ?? false,
                props.inputDisabled,
            );
        // Probably should support
        case "DateMonthCodec":
        case "FutureDateTimeCodec":
        case "IntegerFromStringCodec":
        case "StringCappedCodec":
            return [<Text color="#FF0000">THIS CODEC IS NOT SUPPORTED IF NEEDED IMPLEMENT IN CRMCodecEditForm.tsx</Text>]
        // Intentionally not supporting
        case "DeferDateTimeCodec":
        case "BooleanFromStringCodec":
        case "DecimalFromStringCodec":
        case "MapCodec":
        case "RequiredFromJsonCodec":
        case "TryCatchCodec":
        case "ErrorCodeCodec":
        case "ExactNumberCodec":
        case "FalseCodec":
        case "FileIOCodec":
        case "FormCodec":
        case "FormEditableCodec":
        case "FormIOCodec":
        case "NullCodec":
        case "TrueCodec":
        case "TupleCodec":
        case "UndefinedCodec":
        case "UnknownCodec":
            return []; // LATER handle
        default: 
            return requireExhaustive(props.codec); 
    };
}

const renderRecordForm = <C extends TAnyRecordCodec, M extends TTypeOfNewDefault<C>>(
    propName: string | undefined,
    errorKeyPrefix: string | undefined,
    codec: C,
    model: M,
    onChange: (m: M) => void,
    validationErrors: TError,
    showDeferDateLabels: boolean,
    inputDisabled: boolean = false,
    overrideComps?: {
        [K in keyof M]?: TCRMFormInput<M[K]>
    },
    hideFields?: Array<string>,
    fieldHelp?: Record<string, TCodecEditFormFieldHelp | null | undefined>
) =>
    pipe(
        codec.payload,
        (p) => Object.entries(p),
        (pairs) => pairs.map(([innerPropName, innerCodec]) => {

            if (hideFields && hideFields.includes(innerPropName)) {
                return [];
            }

            const withHelp = (components: Array<JSX.Element>): Array<JSX.Element> => {
                const help = fieldHelp?.[innerPropName];
                return help && components.length > 0
                    ? [
                        <CRMCodecEditFormFieldHelp help={help}>
                            {components.map((component, index) =>
                                <React.Fragment key={index}>
                                    {component}
                                </React.Fragment>
                            )}
                        </CRMCodecEditFormFieldHelp>
                    ]
                    : components;
            };

            // Apply the component override for record types
            if (overrideComps && overrideComps[innerPropName]) {
                return withHelp([
                    overrideComps[innerPropName]({
                        onChange: (prop: unknown) => {
                            return onChange({
                                ...model as {},
                                [innerPropName]: prop
                            } as M);
                        },
                        model: model[innerPropName],
                        validationErrors,
                        disabled: inputDisabled,
                        label: snakeCaseToCopyText(innerPropName)
                    }) as JSX.Element
                ]);
            }
            return withHelp(getListOfFormComponents({
                errorKeyPrefix,
                propName: innerPropName,
                codec: innerCodec,
                model: (model as Record<string, TTypeOfNewDefault<typeof innerCodec>>)[innerPropName],
                validationErrors,
                onChange: (innerV) => onChange({
                    ...pipe(
                        codec.payload,
                        record.keys,
                        (keys) => pipe(
                            model as Record<string, unknown>,
                            record.filterWithIndex((i) => keys.includes(i)),
                        ),
                    ),
                    [innerPropName]: innerV
                } as M),
                showDeferDateLabels: showDeferDateLabels,
                inputDisabled,
            }));
        }),
        array.flatten
    );

const renderIntersectionForm = <C extends TAnyIntersectionCodec, M extends TTypeOfNewDefault<C>>(
    propName: string | undefined,
    errorKeyPrefix: string | undefined,
    codec: C,
    model: M,
    onChange: (m: M) => void,
    validationErrors: TError,
    showDeferDateLabels: boolean,
    inputDisabled: boolean = false
) => {
    return isAnyRecordCodec(codec.payload[0])
        ? pipe(
            codec.payload.map((innerCodec) => getListOfFormComponents({
                codec: innerCodec,
                model: model,
                validationErrors,
                onChange: (v) => onChange(v as M),
                showDeferDateLabels: showDeferDateLabels,
                inputDisabled,
            })),
            array.flatten
        )
        : getListOfFormComponents({
            errorKeyPrefix,
            propName,
            codec: codec.payload[0],
            model: model,
            validationErrors,
            onChange: (v) => onChange(v as M),
            showDeferDateLabels: showDeferDateLabels,
            inputDisabled,
        });
}

const renderArrayForm = <C extends TAnyListCodec, M extends TTypeOfNewDefault<C>>(
    propName: string,
    errorKeyPrefix: string | undefined, 
    codec: C,
    model: M, 
    onChange: (m: M) => void,
    validationErrors: TError,
    showDeferDateLabels: boolean,
    inputDisabled: boolean = false
) => {
    if (codec.payload.codec.type === "UnionCodec" && isUnionOfLiterals(codec.payload.codec)) {
        return [(
            <CRMInputLabelAndErrorWrapComponent label={snakeCaseToCopyText(propName || "")}>
                <CRMMultiSelectDropdownComponent
                    options={codec.payload.codec.payload.map((innerCodec) => ({
                        value: (innerCodec as TAnyLiteralCodec).payload,
                        label: snakeCaseToCopyText((innerCodec as TAnyLiteralCodec).payload)
                    }))}
                    value={model as string[]}
                    onChange={(v) => onChange(v as M)}
                    displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
                    disabled={inputDisabled}
                />
            </CRMInputLabelAndErrorWrapComponent>
        )];
    }

    if (isListOfStrings(codec)) {
        return [
            <CRMAdditiveArrayFormInput 
                label={propNameToLabel(propName)}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
                model={model as Array<string>}
                onChange={onChange as (value: Array<string>) => void}
                disabled={inputDisabled}
            />
        ]
    }

    return (
        (model as Array<unknown>).map((innerModel, i) => 
            <div key={i}>
                <CRMTitleSubSection>{`${propName} ${i + 1}`}</CRMTitleSubSection>
                <CRMSpacer size="medium" />
                <CRMSpacingColumn spacing="medium">
                    {
                        getListOfFormComponents({
                            errorKeyPrefix,
                            codec: (codec as TAnyListCodec).payload.codec,
                            model: innerModel,
                            validationErrors,
                            onChange: (innerV) => {
                                const m = [...model];
                                m[i] = innerV;
                                onChange(m as M);
                            },
                            showDeferDateLabels: showDeferDateLabels,
                            inputDisabled,
                        })
                    }
                </CRMSpacingColumn>
            </div>
        )
    );
}

const renderUnionForm = <C extends TAnyUnionCodec, M extends TTypeOfNewDefault<C>>(
    propName: string | undefined,
    errorKeyPrefix: string | undefined, 
    codec: C, 
    model: M, 
    onChange: (m: M) => void,
    validationErrors: TError,
    showDeferDateLabels: boolean,
    inputDisabled: boolean = false
) => {
    if (isUnionOfLiterals(codec)) {
        return codec.payload.length >= 4 ?
        [
            <CRMDropdownFormInput
                label={propName ? propNameToLabel(propName) : ""}
                model={model as string}
                onChange={onChange as (val: string) => void}
                disabled={inputDisabled}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
                codec={codec}
            />
        ]
        : [
            <CRMRadioButtonsFormInput 
                label={propName ? propNameToLabel(propName) : ""}
                model={model as string}
                onChange={onChange as (val: string) => void}
                disabled={inputDisabled}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
                codec={codec}
            />
        ]
    }

    if (isUnionOfNullAndDecimal(codec)) {
        return pipe(
            codec.payload,
            array.findFirst(({type}) => type === "DecimalCodec"),
            option.fold(
                () => [
                    <CRMPositiveDecimalFormInput
                        label={propName ? propNameToLabel(propName) : ""}
                        model={model as number | null}
                        onChange={onChange as (s: number | null) => void}
                        disabled={inputDisabled}
                        displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
                    />
                ],
                () => [
                    <CRMDecimalFormInput
                        label={propName ? propNameToLabel(propName) : ""}
                        model={model as number | null}
                        onChange={onChange as (s: number | null) => void}
                        disabled={inputDisabled}
                        displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
                    />
                ]
            )
        );
    }

    if (isUnionOfNullAndDeferDateTime(codec)) {
        return showDeferDateLabels ? [
            <CRMDeferInputWithLabelFormInput
                label={propName ? propNameToLabel(propName) : ""}
                model={model  as string | null}
                onChange={onChange as (v: string | null) => void}
                disabled={false}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
            />
        ] : [<CRMDeferInputWithoutLabelFormInput
            label={propName ? propNameToLabel(propName) : ""}
            model={model  as string | null}
            onChange={onChange as (v: string | null) => void}
            disabled={false}
            displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
        />]
    }

    if (isUnionOfNullAndDate(codec)) {
        return [
            <CRMCalendarDateWithoutTimeWithClearFormInput
                label={propName ? propNameToLabel(propName) : ""}
                model={model as string | null}
                onChange={onChange as (v: string | null) => void}
                disabled={inputDisabled}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
            />
        ]
    }

    if (isUnionOfNullAndDateTime(codec)) {
        return [
            <CRMCalendarDateTimeWithClearFormInput 
                label={propName ? propNameToLabel(propName) : ""}
                model={model as string | null}
                onChange={onChange as (v: string | null) => void}
                disabled={inputDisabled}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
            />
        ]
    }

    if (isUnionOfNullAndInteger(codec)) {
        return [
            <CRMTextFormInput
                label={propName ? propNameToLabel(propName) : ""}
                model={model === null ? "" : (model as number).toString()}
                onChange={(s: string) =>  onChange(s === "" ? null as M : parseInt(s) as M)}
                disabled={inputDisabled}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
            />
        ]
        
    }

    if (isUnionOfNullAndCurrencyInteger(codec)) {
        return [
            <CRMCurrencyFormInput 
                label={propName ? propNameToLabel(propName) : ""}
                model={model as number | null}
                onChange={onChange as (v: number | null) => void}
                disabled={inputDisabled}
                mode={propName?.endsWith("_pence") ? "pence" : "pound"}
                displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
            />
        ]
    }

    if (isUnionOfNullAndBoolean(codec)) {
        return [<CRMYesNoBooleanWithClearRadioFormInput 
            label={propName ? propNameToLabel(propName) : ""}
            model={model as boolean | null}
            onChange={onChange as (v: boolean | null) => void}
            disabled={inputDisabled}
            displayError={doesErrorKeyExist(`${errorKeyPrefix ?? "edited"}.${propName}`, validationErrors)}
        />]
    }

    if (isUnionOfNullAndOneOtherType(codec)) {
        const notNullCodec = codec.payload.filter((c) => c.type !== "NullCodec")[0];
        return getListOfFormComponents({
            errorKeyPrefix,
            propName,
            codec: notNullCodec,
            model: model === null
                ? ""
                : model,
            validationErrors,
            onChange: (v) => onChange((v === "" ? null : v) as M),
            showDeferDateLabels: showDeferDateLabels,
            inputDisabled,
        });
    }
    
    return [];
}

const getSpacingChildSizeByColumns = (columns: number) => pipe(
    array.range(1, columns),
    array.map(() => "1fr"),
    (sizeArray) => sizeArray.join(" "), 
);
